// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ImageIcon from '@material-ui/icons/FilterHdr';
import LockIcon from '@material-ui/icons/Lock';
import CloseIcon from '@material-ui/icons/Close';
import { Dialog, IconButton, TextField, FormControlLabel, Switch, Button, Divider, DialogContent, Typography, makeStyles } from '@material-ui/core';

// Internal Dependencies
import Colours from '../../../styles/colours';
import apiService from '../../../services.js/apiService';
import { IsEmpty } from '../../../common/checks';
import { Trim } from '../../../common/util';
import useDebounce from '../../global/use-debounce';

const titleTextSize = 16;

const labelTextSize = 13;

const useStyles = makeStyles(() => ({
  autocompleteRoot: {
    fontSize:               `${labelTextSize}px !important`,
    '& .MuiInputBase-root': {
      fontSize: `${labelTextSize}px !important`,
    },
    '& .MuiAutocomplete-input': {
      fontSize: `${labelTextSize}px !important`,
    },
    '& .MuiAutocomplete-option': {
      fontSize: `${labelTextSize}px !important`,
    },
  },
}));

const StampDialog = ({
  onCloseDialog, onCloseDialogEnd, loyaltyStamps, onUpdateLoyaltyStamps, handleCustomizeInput,
  handleDialogSave, handleImageChange, imageFormats, fileInputRefs, isDeactivated }) => {
  const [existingPages, setExistingPages] = useState([]);
  const debouncedSearch = useDebounce(loyaltyStamps.dialog.linkPageName, 300);

  const classes = useStyles();

  const setLinkPage = (linkPageId, linkPageName) => {
    onUpdateLoyaltyStamps({ dialog: { linkPageId, linkPageName } });
  };

  const searchPages = async (name) => {
    try {
      if (!IsEmpty(Trim(name))) {
        let pages = await apiService.getPagesSuggest({ key: name, withLiveClaims: false, limit: 10 });
        // Prevent `Monstyr` from showing
        pages = pages.filter((d) => d.name !== 'Monstyr');
        setExistingPages(pages);
      } else {
        setExistingPages([]);
      }
    } catch (error) {
      setExistingPages([]);
    }
  };

  useEffect(() => {
    searchPages(loyaltyStamps.dialog.linkPageName);
  }, [debouncedSearch]);

  return (
    <Dialog
      onClose={onCloseDialog}
      open={loyaltyStamps.dialog.open}
      onTransitionEnd={onCloseDialogEnd}
      PaperProps={{ className: 'w-100 d-flex flex-column align-items-center mx-2' }}
    >
      <DialogContent dividers={false} className="px-1 px-md-4 py-3" style={{ overflowX: 'hidden' }}>
        <div className="w-100 p-sm-3">
          <div className="container ml-5 ml-sm-2">
            <div className="row mb-3">
              <div className="col-10 col-sm-12 pl-0 pl-sm-0 pr-1 pr-sm-0 d-flex align-items-center justify-content-between">
                <Typography style={{ fontSize: titleTextSize }} className="pl-0 pl-sm-2 font-weight-bold">Customise Stamp</Typography>
                <IconButton onClick={onCloseDialog} size="small">
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-12 col-sm-4 pl-0 pl-sm-2 mb-2 mb-sm-0 d-flex flex-column justify-content-center">
                <Typography style={{ fontSize: labelTextSize }}>Stamp Image</Typography>
                <Typography style={{ fontSize: labelTextSize }}>(Max 1MB)</Typography>
              </div>
              <div className="col-12 col-sm-8 d-flex align-items-center pl-2">
                <input
                  hidden
                  name="stampImage"
                  accept={imageFormats}
                  type="file"
                  onChange={handleImageChange}
                  ref={fileInputRefs.stampImage}
                />
                <IconButton
                  onClick={() => fileInputRefs.stampImage.current.click()}
                  size="small"
                  fontSize={50}
                  disabled={isDeactivated}
                  className="mb-4 p-0"
                  style={{ width: 180, height: 180, backgroundColor: Colours.Gray4, fontSize: '40pt' }}
                >
                  {
                    !IsEmpty(loyaltyStamps.dialog.stampImage.data) ? (
                      <img
                        src={loyaltyStamps.dialog.stampImage.data}
                        alt="Customized Stamp"
                        className="w-100 rounded-circle"
                      />
                    ) : <ImageIcon fontSize="large" htmlColor={Colours.Black} />
                  }
                </IconButton>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-12 col-sm-4 pl-0 pl-sm-2 d-flex align-items-center">
                <Typography style={{ fontSize: labelTextSize }}>Label:</Typography>
              </div>
              <div className="col-12 col-sm-8 d-flex align-items-center pl-0 pl-sm-2">
                <TextField
                  onChange={handleCustomizeInput}
                  variant="outlined"
                  size="small"
                  className="w-75"
                  disabled={isDeactivated}
                  value={loyaltyStamps.dialog.name}
                  inputProps={{ style: { fontSize: labelTextSize } }}
                  name="name"
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-12 col-sm-4 pl-0 pl-sm-2 d-flex align-items-center">
                <Typography style={{ fontSize: labelTextSize }}>Sub-label:</Typography>
              </div>
              <div className="col-12 col-sm-8 d-flex align-items-center pl-0 pl-sm-2">
                <TextField
                  onChange={handleCustomizeInput}
                  variant="outlined"
                  size="small"
                  className="w-75"
                  disabled={isDeactivated}
                  value={loyaltyStamps.dialog.label}
                  inputProps={{ style: { fontSize: labelTextSize } }}
                  name="label"
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-12 col-sm-4 pl-0 pl-sm-2 d-flex align-items-center">
                <Typography>Link Page ID</Typography>
              </div>
              <div className="col-12 col-sm-8 d-flex align-items-center pl-0 pl-sm-2">
                <TextField
                  onChange={handleCustomizeInput}
                  variant="outlined"
                  size="small"
                  className="w-75"
                  disabled={isDeactivated}
                  value={loyaltyStamps.dialog.linkPageId}
                  inputProps={{ style: { fontSize: labelTextSize } }}
                  name="linkPageId"
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-12 col-sm-4 pl-0 pl-sm-2 d-flex align-items-center">
                <Typography style={{ fontSize: labelTextSize }}>Linked to brand:</Typography>
              </div>
              <div className="col-12 col-sm-8 d-flex align-items-center pl-0 pl-sm-2">
                <Autocomplete
                  className={`${classes.autocompleteRoot} w-75`}
                  freeSolo
                  options={existingPages}
                  size="small"
                  defaultValue=""
                  disabled={isDeactivated}
                  value={{ name: loyaltyStamps.dialog.linkPageName }}
                  getOptionLabel={(option) => (option.name ? option.name : '')}
                  onChange={(e, v) => {
                    if (!IsEmpty(v)) {
                      setLinkPage(v.id, v.name);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      className="w-100"
                      value={{ name: loyaltyStamps.dialog.linkPageName }}
                      onChange={(e) => setLinkPage(null, e.target.value)}
                    />
                  )}
                />
              </div>
            </div>
            <div className="row mb-4">
              <div className="col-12 col-sm-4 pl-0 pl-sm-2 mt-0 mt-md-2">
                <Typography style={{ fontSize: labelTextSize }}>
                  Issued by linked brand:
                </Typography>

              </div>
              <div className="col-12 col-sm-8 d-flex flex-column pl-0 pl-sm-2">
                <div className="w-100 d-flex align-items-center">
                  <FormControlLabel
                    className="mr-1"
                    style={{ fontSize: labelTextSize }}
                    disabled={isDeactivated}
                    control={(
                      <Switch
                        onChange={handleCustomizeInput}
                        checked={loyaltyStamps.dialog.isToBeIssueByLinkPageId}
                        color="secondary"
                        name="isToBeIssueByLinkPageId"
                      />
                    )}
                    label={<Typography style={{ fontSize: labelTextSize }}>{loyaltyStamps.dialog.isToBeIssueByLinkPageId ? 'Yes' : 'No'}</Typography>}
                  />
                  {loyaltyStamps.dialog.isToBeIssueByLinkPageId && <LockIcon className="pb-2" fontSize="medium" />}
                </div>
                <Typography
                  className="w-75"
                  style={{ fontSize: 11 }}
                >
                  Select yes only if staff from the linked brand’s participating outlets can issue the stamp.
                </Typography>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-12 pl-0 pl-sm-2 mt-0 mt-md-2">
                <Divider />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-12 pl-0 pl-sm-2 mt-0 mt-md-2">
                <Typography style={{ fontSize: titleTextSize }} className="align-self-start mb-2 pl-0 pl-sm-2 font-weight-bold">
                  Prize / Promotion
                </Typography>

              </div>
            </div>

            <div className="row mb-4">
              <div className="col-12 col-sm-4 pl-0 pl-sm-2 d-flex flex-column justify-content-center">
                <Typography style={{ fontSize: labelTextSize }}>
                  Image
                </Typography>
                <Typography style={{ fontSize: labelTextSize }}>
                  (Max 2MB)
                </Typography>
              </div>
              <div className="col-12 col-sm-8 d-flex flex-column pl-0 pl-sm-2">
                <input
                  hidden
                  name="prizeImage"
                  accept={imageFormats}
                  type="file"
                  onChange={handleImageChange}
                  ref={fileInputRefs.prizeImage}
                />
                <Button
                  onClick={() => fileInputRefs.prizeImage.current.click()}
                  className="p-0"
                  disabled={isDeactivated}
                  style={{
                    width:           '75%',
                    height:          '9rem',
                    backgroundColor: IsEmpty(loyaltyStamps.dialog.prizeImage.data) ? Colours.Gray4 : null,
                  }}
                >
                  {IsEmpty(loyaltyStamps.dialog.prizeImage.data)
                    ? <ImageIcon fontSize="large" htmlColor={Colours.Black} />
                    : (
                      <img
                        src={loyaltyStamps.dialog.prizeImage.data}
                        alt="Prize"
                        className="w-100 rounded"
                        style={{ height: '9rem', objectFit: 'cover' }}
                      />
                    )}
                </Button>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-12 col-sm-4 pl-0 pl-sm-2 d-flex align-items-center">
                <Typography style={{ fontSize: labelTextSize }}>
                  Prize / Promo Text
                </Typography>
              </div>
              <div className="col-10 col-sm-8 d-flex flex-column pl-0 pl-sm-2">
                <TextField
                  multiline
                  rows={4}
                  onChange={handleCustomizeInput}
                  disabled={isDeactivated}
                  variant="outlined"
                  size="small"
                  className="w-100"
                  inputProps={{ style: { fontSize: labelTextSize } }}
                  value={loyaltyStamps.dialog.prize}
                  name="prize"
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-12 col-sm-4 pl-0 pl-sm-2 d-flex align-items-center">
                <Typography style={{ fontSize: labelTextSize }}>
                  Terms & Conditions
                </Typography>
              </div>
              <div className="col-10 col-sm-8 d-flex flex-column pl-0 pl-sm-2">
                <TextField
                  multiline
                  rows={4}
                  onChange={handleCustomizeInput}
                  disabled={isDeactivated}
                  variant="outlined"
                  size="small"
                  className="w-100"
                  inputProps={{ style: { fontSize: labelTextSize } }}
                  value={loyaltyStamps.dialog.prizeTerms}
                  name="prizeTerms"
                />
              </div>
            </div>
          </div>
          <div className="w-100 d-flex justify-content-center align-items-center">
            <Button disabled={isDeactivated} variant="contained" color="secondary" className="w-50" onClick={handleDialogSave}>Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

StampDialog.propTypes = {
  onCloseDialog:         PropTypes.func.isRequired,
  onCloseDialogEnd:      PropTypes.func.isRequired,
  loyaltyStamps:         PropTypes.shape(Object).isRequired,
  onUpdateLoyaltyStamps: PropTypes.func.isRequired,
  handleCustomizeInput:  PropTypes.func.isRequired,
  handleDialogSave:      PropTypes.func.isRequired,
  handleImageChange:     PropTypes.func.isRequired,
  fileInputRefs:         PropTypes.shape(Object).isRequired,
  imageFormats:          PropTypes.arrayOf(PropTypes.string).isRequired,
  isDeactivated:         PropTypes.bool.isRequired,
};

export default StampDialog;
